import { json, type DataFunctionArgs } from '@remix-run/node'
import { useActionData, useSearchParams } from '@remix-run/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { AuthLayout } from '~/components/layout/auth-layout.tsx'
import {
  authenticator,
  requireAnonymous,
  signup,
} from '~/features/auth/auth.server.ts'
import { commitSession, getSession } from '~/utils/config/session.server.ts'
import { redirectWithConfetti } from '~/features/toast/flash-session.server.ts'
import { validateCSRF } from '~/utils/csrf.server.ts'
import { checkHoneypot } from '~/utils/honeypot.server.ts'
import { useIsPending } from '~/utils/misc.tsx'
import { parseWithZod } from '@conform-to/zod'
import { prisma } from '~/utils/config/db.server.ts'
import {
  SignupForm,
  SignupFormSchema,
} from '~/components/forms/singup-form.tsx'
import { Prisma } from '@prisma/client'
import { CompanyTypeEnum } from 'types/enums.ts'

export async function loader({ request }: DataFunctionArgs) {
  await requireAnonymous(request)
  const session = await getSession(request.headers.get('cookie'))
  const error = session.get(authenticator.sessionErrorKey)
  const message = error?.message ?? null
  return json(
    { formError: typeof message === 'string' ? message : null },
    { headers: { 'Set-Cookie': await commitSession(session) } },
  )
}

export async function action({ request }: DataFunctionArgs) {
  const formData = await request.formData()
  await validateCSRF(formData, request.headers)
  checkHoneypot(formData)

  const submission = await parseWithZod(formData, {
    schema: SignupFormSchema.superRefine(async ({ company, email }, ctx) => {
      const existingCompany = await prisma.company.findFirst({
        where: {
          name: {
            equals: company,
            mode: Prisma.QueryMode.insensitive,
          },
        },
      })

      if (existingCompany) {
        ctx.addIssue({
          path: ['company'],
          code: 'custom',
          message:
            'Ya existe una empresa con este nombre, póngase en contacto con el administrador de su compañía',
        })
      }

      const existingInvitation = await prisma.invitation.findFirst({
        where: {
          email,
          status: 'PENDING',
        },
      })

      if (existingInvitation) {
        ctx.addIssue({
          path: ['email'],
          code: 'custom',
          message: 'Este correo ya tiene una invitación pendiente',
        })
      }

      const existingUser = await prisma.user.findFirst({
        where: { email },
        select: { id: true },
      })

      if (existingUser) {
        ctx.addIssue({
          path: ['email'],
          code: 'custom',
          message: 'Ya existe un usuario con este email',
        })
      }
    }),
    async: true,
  })

  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const {
    email,
    firstName,
    password,
    phone,
    lastName,
    company,
    redirectTo,
    billingEmail,
  } = submission.value

  await signup({
    email,
    password,
    firstName,
    lastName,
    phone,
    company,
    billingEmail,
    companyType: CompanyTypeEnum.SHIPPER,
  })

  return redirectWithConfetti(safeRedirect(redirectTo, '/'))
}

export default function SignUpPage() {
  const [searchParams] = useSearchParams()
  const actionData = useActionData<typeof action>()
  const isPending = useIsPending()
  const redirectTo = searchParams.get('redirectTo') || '/'

  return (
    <AuthLayout className="min-h-screen">
      <SignupForm
        actionData={actionData}
        isPending={isPending}
        redirectTo={redirectTo}
      />
    </AuthLayout>
  )
}
